<template>
  <div
    v-if="marquees"
    class="catalog-marquees-component"
  >
    <div class="default-marquees">
      <div class="marquees">
        <div
          v-for="marquee in marquees"
          :key="marquee.id"
          :class="{'marquee': true, 'active': isSelectedMarquee(marquee.id)}"
          @mouseover="handleSelectMarquee(marquee)"
        >
          <img :src="getAttachmentUrl(marquee.iconId)">
          <p class="all-upper-case">
            {{ marquee.title }}
          </p>
        </div>
      </div>
      <div
        class="marquee-image-container"
      >
        <img :src="getAttachmentUrl(selectedMarquee.imageId)">
        <div
          class="marquee-description"
        >
          <p>{{ stripHtml(selectedMarquee.description) }}</p>
        </div>
      </div>
    </div>
    <div class="marquee-carousel-wrapper">
      <carousel
        :tile-count="tileCount"
        tile-type="catalog-marquee"
        :item-width="352"
        @scroll-event="handleCarouselScroll"
      >
        <div
          v-for="marquee in marquees"
          :key="marquee.id"
          :ref="'marqueeCards'"
          class="marquee-card"
        >
          <div class="marquee-card-content">
            <div class="title-wrapper">
              <img :src="getAttachmentUrl(marquee.iconId)">
              <p>{{ marquee.title }}</p>
            </div>
            <div class="content-wrapper">
              <img :src="getAttachmentUrl(marquee.imageId)">
              <div
                class="description-wrapper"
              >
                <p>{{ stripHtml(marquee.description) }}</p>
              </div>
            </div>
          </div>
        </div>
      </carousel>
      <pagination-indicator
        :active-index="activeMarqueeIndex"
        :total="marquees.length"
      />
    </div>
  </div>
</template>

<script>
import PaginationIndicator from '@components/ui/pagination-indicator';
import Carousel from '@components/carousels/carousel';

import { stripHtml } from '@/utils';

export default {
  components: { Carousel, PaginationIndicator },
  props: {
    catalogPage: {
      type: Object,
    },
    marquees: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      activeMarqueeIndex: 0, // This is only used at mobile screen sizes
      selectedMarquee: this.marquees[0] || null,
      timer: null,
    };
  },
  computed: {
    tileCount() {
      return this.marquees.length;
    },
  },
  mounted() {
    this.timer = setInterval(this.rotateMarquee, 5000);
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  },
  methods: {
    getAttachmentUrl(attachmentId) {
      return this.$store.getters['attachments/get'][attachmentId]?.url;
    },
    isSelectedMarquee(id) {
      return !!(id === this.selectedMarquee.id);
    },
    handleCarouselScroll({ position, width }) {
      // This is the logic that we use to determine the scroll position of the middle of the carousel
      // The carousel will emit the position of the left scroll bound and the width of the carousel container
      // We can take half the width, add it to the position and get approximately the center of the screen
      // We can use any of the refs on the marqueeCards to get the current width of the card and by dividing
      // the visible middle of the screen by the width of the tile plus the margin, we can determine if
      // the left bound of a given tile has crossed the center threshold and if it has, we can update active
      // index to that of the tile crossing the middle.
      const firstCard = this.$refs.marqueeCards?.[0];
      const cardWidth = firstCard?.getBoundingClientRect()?.width;
      if (cardWidth) {
        const widthWithMargin = cardWidth + 16;
        const visibleMiddle = position + (width / 2);
        this.activeMarqueeIndex = Math.floor(visibleMiddle / widthWithMargin);
      }
    },
    handleSelectMarquee(marquee) {
      if (this.timer) {
        clearInterval(this.timer);
      }
      this.selectedMarquee = marquee;
      this.sendAnalytics(marquee);
    },
    rotateMarquee() {
      let marquee;
      if (this.selectedMarquee) {
        const currentIndex = this.marquees.findIndex(marq => marq.id === this.selectedMarquee.id);
        marquee = this.marquees[currentIndex + 1] || this.marquees[0];
      } else {
        marquee = this.marquees[0];
      }
      this.selectedMarquee = marquee;
    },
    sendAnalytics(marquee) {
      this.$analytics.events.viewedAddOnBenefits(this.catalogPage, marquee);
    },
    stripHtml,
  },
};
</script>

<style lang="scss" scoped>
.catalog-marquees-component {
  margin-bottom: 5rem;

  .default-marquees {
    display: flex;
    flex-wrap: nowrap;
    min-height: rem-calc(410);
    justify-content: space-between;

    .marquees {
      display: flex;
      flex-direction: column;
      justify-content: space-around;
      .marquee {
        display: flex;
        align-items: center;
        opacity: 0.7;
        color: #000000;
        cursor: pointer;

        img {
          width: rem-calc(20);
          height: rem-calc(20);
        }
        p {
          padding-left: 1rem;
          line-height: 1.5;
        }
        .all-upper-case {
          text-transform: uppercase;
        }
      }
      .active {
        opacity: 1;
        font-weight: $bold;
      }
    }
    .marquee-image-container {
      display: flex;
      flex-direction: column;
      max-width: rem-calc(480);
      border-radius: 4px;
      border: solid 1px #d8d8d8;

      img {
        width: 100%;
        border-radius: 4px 4px 0 0;
        border-bottom: solid 1px #d8d8d8;
      }

      .marquee-description {
        height: 100%;
        display: flex;
        align-items: center;
        padding: 1rem;
        font-size: rem-calc(14);
        font-weight: $semi-bold;
        line-height: 1.14;
        text-align: center;
        color: #000000;
        margin: 0 auto;

        p {
          margin: 0 !important;
        }
      }
    }
  }

  .marquee-carousel-wrapper {
    display: none;

    /deep/ .tile-container {
      height: rem-calc(291);
    }

    .marquee-card {
      display: inline-flex;
      width: rem-calc(336);
      max-width: 80%;
      margin-right: 1rem;
      border-radius: 4px;
      white-space: initial;
      height: 100%;

      .marquee-card-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        font-size: rem-calc(16);
        line-height: 1.43;

        .title-wrapper {
          align-items: center;
          box-sizing: border-box;
          color: #000000;
          display: flex;
          height: rem-calc(64);
          justify-content: flex-start;
          padding: 0 1rem 1rem 1rem;
          width: 100%;

          img {
            width: rem-calc(20);
            height: rem-calc(20);
            flex-shrink: 0;
          }

          p {
            padding-left: 1rem;
            line-height: 1.5;
            font-weight: $bold;
            margin: 0;
          }
        }

        .content-wrapper {
          display: flex;
          flex-direction: column;
          flex: 1;
          border-radius: 4px;
          border: solid 0.7px #d8d8d8;

          img {
            border-radius: 4px 4px 0 0;
            border-bottom: solid 0.7px #d8d8d8;
          }

          .description-wrapper {
            flex: 1;
            display: flex;
            justify-content: center;
            align-items: center;
            text-align: center;
            padding: 1rem;

            p {
              margin: 0;
              font-size: rem-calc(14);
              font-weight: $semi-bold;
            }
          }
        }
      }
    }
  }


  @include breakpoint(medium) {
    .default-marquees {
      display: none;
    }
    .marquee-carousel-wrapper {
      display: block;
    }
  }
}
</style>
